import { useEffect } from "react";
import { createPortal } from "react-dom";
import { C } from "@/lib/brand";

// Right-hand drawer used to preview supporting content (program
// guidelines, brand voice agreement, etc.) without sending the user
// away from a flow they're in the middle of. Slides from the right,
// dims the page behind it, ESC and backdrop click both dismiss.
//
// Props:
//   open       — controls visibility
//   onClose    — called when the user dismisses
//   title      — bold caps title at the top of the drawer
//   eyebrow    — optional small caps label above the title (e.g. "★ GUIDE")
//   children   — body content (any React node)
//   width      — drawer width in px (default 520; clamps to viewport)
//   testId     — root data-testid (default right-drawer)
export default function RightDrawer({
  open,
  onClose,
  title,
  eyebrow,
  children,
  width = 520,
  testId = "right-drawer",
}) {
  // ESC to dismiss + lock the body scroll while the drawer is open so
  // the page behind doesn't move under the user's finger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <button
        type="button"
        aria-label="Close drawer"
        data-testid={`${testId}-backdrop`}
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,14,13,0.55)",
          border: 0,
          padding: 0,
          cursor: "pointer",
        }}
      />
      <aside
        style={{
          position: "relative",
          background: C.cream,
          color: C.ink,
          width: `min(${width}px, 100vw)`,
          height: "100vh",
          overflowY: "auto",
          boxShadow: "-8px 0 32px rgba(15,14,13,0.18)",
          animation: "voya9-drawer-slide-in 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <header
          style={{
            position: "sticky",
            top: 0,
            background: C.cream,
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${C.warmGray3}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            zIndex: 1,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow && (
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
                {eyebrow}
              </div>
            )}
            <h2
              id={`${testId}-title`}
              style={{
                margin: eyebrow ? "8px 0 0" : 0,
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.01em",
                color: C.ink,
                lineHeight: 1.2,
              }}
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testId}-close`}
            aria-label="Close"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: C.warmGray,
              cursor: "pointer",
              padding: 4,
            }}
          >
            CLOSE ✕
          </button>
        </header>
        <div style={{ padding: "20px 24px 60px" }}>{children}</div>
      </aside>

      <style>{`
        @keyframes voya9-drawer-slide-in {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1;   }
        }
      `}</style>
    </div>,
    document.body,
  );
}
